import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';
import ManufacturerList from './ManufacturerList';
import ManufacturerForm from './ManufacturerForm';
import axios from 'axios';

// API URL configuration - uses relative URL in production (nginx proxies to backend)
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use(config => {
  const token = safeStorage.getItem('authToken');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

const ManufacturerScreen = () => {
  const { isReadOnly } = useAuth();
  const [manufacturers, setManufacturers] = useState([]);
  const [editingManufacturer, setEditingManufacturer] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({
    manufacturer_name: '',
    manufacturer_address: '',
    manufacturer_contact_info: ''
  });

  const readOnlyMode = isReadOnly('/manufacturers');

  const fetchManufacturers = async () => {
    const params = {};
    Object.keys(filters).forEach(key => {
      if (filters[key]) params[key] = filters[key];
    });
    const res = await api.get('/manufacturers', { params });
    setManufacturers(res.data);
  };

  useEffect(() => {
    fetchManufacturers();
    // eslint-disable-next-line
  }, [filters]);

  const handleCreate = () => {
    if (readOnlyMode) return;
    setEditingManufacturer(null);
    setShowForm(true);
  };

  const handleEdit = manufacturer => {
    if (readOnlyMode) return;
    setEditingManufacturer(manufacturer);
    setShowForm(true);
  };

  const handleDelete = async id => {
    if (readOnlyMode) return;
    
    if (!window.confirm('Are you sure you want to delete this manufacturer?')) {
      return;
    }
    
    try {
      await api.delete(`/manufacturers/${id}`);
      fetchManufacturers();
    } catch (error) {
      console.error('Error deleting manufacturer:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete manufacturer';
      
      // Check if it's a foreign key constraint error
      if (errorMessage.includes('foreign key') || errorMessage.includes('violates') || error.response?.status === 409) {
        alert('Cannot delete this manufacturer because it has amplifiers linked to it.\n\nPlease delete or reassign all amplifiers first, then try again.');
      } else {
        alert(`Error deleting manufacturer: ${errorMessage}`);
      }
    }
  };

  const handleSave = async formData => {
    if (editingManufacturer) {
      await api.put(`/manufacturers/${editingManufacturer.manufacturer_id}`, formData);
    } else {
      await api.post('/manufacturers', formData);
    }
    fetchManufacturers();
    setShowForm(false);
    setEditingManufacturer(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingManufacturer(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters(f => ({ ...f, [field]: value }));
  };

  return (
    <div>
      <h1>Manufacturers</h1>
      {!readOnlyMode && (
        <button onClick={handleCreate}>Create New Manufacturer</button>
      )}
      {readOnlyMode && (
        <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1rem' }}>
          Read-only mode: You can view but not modify manufacturers
        </p>
      )}
      {showForm && !readOnlyMode && (
        <ManufacturerForm
          manufacturer={editingManufacturer}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      )}
      <ManufacturerList
        manufacturers={manufacturers}
        filters={filters}
        onFilterChange={handleFilterChange}
        onEdit={readOnlyMode ? null : handleEdit}
        onDelete={readOnlyMode ? null : handleDelete}
        readOnly={readOnlyMode}
      />
    </div>
  );
};

export default ManufacturerScreen;